import { useConnectionsQuery } from './api.js';

/**
 * A mailbox whose access ended, said on every screen — not only under Settings.
 *
 * When Google or Microsoft ends a grant, capture stops quietly: nothing new reaches
 * Review, and nothing on Review says why. The connection card already explains it, but
 * nobody looks at Settings to find out that labelled mail has stopped arriving. This is
 * the same fact, where the person is. It reconnects nothing itself: the action opens the
 * Connections card, whose Reconnect keeps the same mailbox, its history and its Review.
 */

const COPY: Record<string, { name: string; marked: string }> = {
  gmail: { name: 'Gmail', marked: 'labeled emails' },
  microsoft: { name: 'Outlook', marked: 'categorized emails' },
};

export function MailboxReconnectBanner({ canReconnect, onOpenConnections }: {
  /** Workspace admins reconnect; anyone else is told who can. */
  canReconnect: boolean;
  onOpenConnections: () => void;
}) {
  const { data } = useConnectionsQuery();
  const dead = (data?.connectors ?? []).find((c) => COPY[c.kind] && c.status === 'REAUTH_REQUIRED');
  if (!dead) return null;
  const copy = COPY[dead.kind]!;
  return (
    <div
      role="status" data-mailbox-reconnect={dead.kind}
      className="mb-6 flex min-w-0 flex-wrap items-center justify-between gap-3 rounded-md bg-amber-50 px-4 py-3 text-sm text-amber-900 ring-1 ring-amber-600/20"
    >
      <div className="min-w-0 [overflow-wrap:anywhere]">
        <p className="font-medium">{copy.name} needs to be reconnected.</p>
        <p>
          Your {copy.name} connection expired. Reconnect to continue capturing {copy.marked}.
          {canReconnect ? '' : ' Ask a workspace admin to reconnect it.'}
        </p>
      </div>
      {canReconnect ? (
        <button
          type="button" onClick={onOpenConnections}
          className="inline-flex min-h-11 shrink-0 items-center rounded-md bg-slate-900 px-4 text-sm font-medium text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
        >
          Reconnect {copy.name}
        </button>
      ) : null}
    </div>
  );
}
